import React, { useEffect, useState } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Spinner, 
  Badge, 
  ListGroup, 
  Accordion, 
  Form, 
  Button, 
  Dropdown,
  Modal,
} from "react-bootstrap";
import { createClient } from "@supabase/supabase-js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Client Supabase
const supabase = createClient(
  "https://xecpjoetpimjupcoetmp.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhlY3Bqb2V0cGltanVwY29ldG1wIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MDg4MDIsImV4cCI6MjA2NjA4NDgwMn0.l3mHF5-IsZlYB4LawylJXw3JSgWp2rzIw5GkTlxRRoU"
);

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formState, setFormState] = useState({
    client_id: "",
    start_date: "",
    end_date: "",
    invoice_items: [{ description: "", quantity: 1, price_per_item: "" }]
  });

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("ro-RO", options);
  };

  const calculateTotals = (invoiceItems) => {
    let totalItems = 0;
    let totalPrice = 0;
    let hasPrice = false;

    if (!invoiceItems) return { totalItems: 0, totalPrice: null };

    invoiceItems.forEach(item => {
      totalItems += parseInt(item.quantity, 10) || 0;
      if (item.price_per_item) {
        hasPrice = true;
        totalPrice += (parseInt(item.quantity, 10) || 0) * (parseFloat(item.price_per_item) || 0);
      }
    });

    return { totalItems, totalPrice: hasPrice ? totalPrice.toFixed(2) : null };
  };

  const fetchInvoices = async () => {
    setLoading(true);
    let query = supabase.from("invoices").select(`
      *,
      invoice_items (
        id,
        description,
        quantity,
        price_per_item
      ),
      clients (
        id,
        name
      )
    `);

    if (selectedClient) {
      query = query.eq("client_id", selectedClient);
    }
    if (dateRange.start && dateRange.end) {
      query = query.gte("start_date", dateRange.start).lte("end_date", dateRange.end);
    }
    
    const { data, error } = await query.order("start_date", { ascending: false });

    if (!error) {
      setInvoices(data);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    const { data, error } = await supabase.from("clients").select("id, name");
    if (!error) {
      setClients(data);
    }
  };

  const handleShowModal = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setFormState({
        client_id: invoice.clients.id,
        start_date: invoice.start_date,
        end_date: invoice.end_date,
        invoice_items: invoice.invoice_items.length > 0 ? invoice.invoice_items.map(item => ({
          ...item,
          description: item.description || "",
          quantity: item.quantity || 1,
          price_per_item: item.price_per_item || ""
        })) : [{ description: "", quantity: 1, price_per_item: "" }]
      });
    } else {
      setEditingInvoice(null);
      setFormState({
        client_id: "",
        start_date: "",
        end_date: "",
        invoice_items: [{ description: "", quantity: 1, price_per_item: "" }]
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingInvoice(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...formState.invoice_items];
    newItems[index][name] = value;
    setFormState(prev => ({ ...prev, invoice_items: newItems }));
  };

  const handleAddItem = () => {
    setFormState(prev => ({
      ...prev,
      invoice_items: [...prev.invoice_items, { description: "", quantity: 1, price_per_item: "" }]
    }));
  };

  const handleRemoveItem = (index) => {
    const newItems = formState.invoice_items.filter((_, i) => i !== index);
    setFormState(prev => ({ ...prev, invoice_items: newItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formState.client_id || !formState.start_date || !formState.end_date) {
      alert("Vă rugăm să completați toate câmpurile obligatorii.");
      return;
    }
    
    const itemsToSave = formState.invoice_items.filter(item => item.description.trim() !== "");

    if (editingInvoice) {
      await handleEditInvoice(editingInvoice.id, itemsToSave);
    } else {
      await handleAddInvoice(itemsToSave);
    }
    handleCloseModal();
    fetchInvoices();
  };

  const handleAddInvoice = async (itemsToSave) => {
    const { data: invoiceData, error: invoiceError } = await supabase
      .from("invoices")
      .insert({
        client_id: formState.client_id,
        start_date: formState.start_date,
        end_date: formState.end_date,
      })
      .select();

    if (invoiceError) return;

    if (itemsToSave.length > 0) {
      const itemsWithInvoiceId = itemsToSave.map(item => ({
        ...item,
        invoice_id: invoiceData[0].id,
        price_per_item: item.price_per_item || null
      }));
      await supabase.from("invoice_items").insert(itemsWithInvoiceId);
    }
  };

  const handleEditInvoice = async (invoiceId, itemsToSave) => {
    await supabase
      .from("invoices")
      .update({
        client_id: formState.client_id,
        start_date: formState.start_date,
        end_date: formState.end_date,
      })
      .eq("id", invoiceId);
    
    await supabase.from("invoice_items").delete().eq("invoice_id", invoiceId);

    if (itemsToSave.length > 0) {
      const itemsWithInvoiceId = itemsToSave.map(item => ({
        ...item,
        id: item.id || crypto.randomUUID(),
        invoice_id: invoiceId,
        price_per_item: item.price_per_item || null
      }));
      await supabase.from("invoice_items").insert(itemsWithInvoiceId);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm("Ești sigur că vrei să ștergi această factură?")) {
      await supabase.from("invoice_items").delete().eq("invoice_id", invoiceId);
      await supabase.from("invoices").delete().eq("id", invoiceId);
      fetchInvoices();
    }
  };

  // ✅ Funcția PDF corectă cu pagini multiple
  const handleDownloadPDF = (invoice) => {
    const input = document.getElementById(`invoice-content-${invoice.id}`);
    if (!input) {
      alert("Nu s-a putut găsi conținutul facturii pentru a genera PDF-ul.");
      return;
    }

    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`lista-${invoice.clients.name}-${invoice.start_date}-${invoice.end_date}.pdf`);
    });
  };

  useEffect(() => {
    fetchClients();
    fetchInvoices();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [selectedClient, dateRange]);

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">Facturi</h3>
        <Button onClick={() => handleShowModal()} variant="primary">Adaugă Factură</Button>
      </div>

      <Card className="p-4 mb-4 neumorphic-card">
        <h5>Filtrează Facturi</h5>
        <Form>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Client</Form.Label>
                <Form.Select 
                  value={selectedClient || ""} 
                  onChange={(e) => setSelectedClient(e.target.value || null)}
                >
                  <option value="">Toți Clienții</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Dată Început</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dateRange.start || ""}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Dată Sfârșit</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dateRange.end || ""}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card>
      
      <h4 className="mb-4">Listă Facturi</h4>
      {loading ? (
        <div className="text-center w-100"><Spinner animation="border" /></div>
      ) : (
        <Accordion> 
          {invoices.length > 0 ? (
            invoices.map((invoice) => (
              <Accordion.Item eventKey={invoice.id} key={invoice.id} className="mb-3 neumorphic-card border-0">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <span className="fw-bold fs-5">{invoice.clients.name}</span>
                      <Badge pill bg="info" className="ms-3 text-dark">
                        {formatDate(invoice.start_date)} - {formatDate(invoice.end_date)}
                      </Badge>
                    </div>
                    <div className="pe-2" onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm">⋮</Dropdown.Toggle>
                          <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => handleShowModal(invoice)}>Editează</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDownloadPDF(invoice)}>Descarcă PDF</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDeleteInvoice(invoice.id)} className="text-danger">Șterge</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div id={`invoice-content-${invoice.id}`} className="p-4">
                    <h5 className="mb-3">Listă Operațiuni efectuate</h5>
                    <p>De la {invoice.start_date} la {invoice.end_date}</p>
                    <ListGroup variant="flush" className="mb-4 border-top border-bottom">
                      {invoice.invoice_items.length > 0 ? (
                        invoice.invoice_items.map((item) => (
                          <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center py-3">
                            <span>{item.description}</span>
                            <div className="d-flex align-items-center">
                              <span className="fw-bold me-3">{item.quantity} buc.</span>
                              {item.price_per_item && (
                                <Badge bg="success">{item.price_per_item} Lei/buc.</Badge>
                              )}
                            </div>
                          </ListGroup.Item>
                        ))
                      ) : (
                        <p className="text-muted p-2">Nu există itemuri pentru această factură.</p>
                      )}
                    </ListGroup>

                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold fs-5">Total Elemente:</span>
                        <span className="fw-bold fs-5 text-primary">
                          {calculateTotals(invoice.invoice_items).totalItems}
                        </span>
                      </div>
                      {calculateTotals(invoice.invoice_items).totalPrice && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold fs-5">Total Preț:</span>
                          <span className="fw-bold fs-5 text-success">
                            {calculateTotals(invoice.invoice_items).totalPrice} Lei
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))
          ) : (
            <p className="text-center text-muted mt-5">Nu s-au găsit facturi conform filtrelor.</p>
          )}
        </Accordion>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingInvoice ? "Editează Factura" : "Adaugă Factură Nouă"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Select name="client_id" value={formState.client_id} onChange={handleFormChange} required>
                    <option value="">Selectează un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dată Început</Form.Label>
                  <Form.Control type="date" name="start_date" value={formState.start_date} onChange={handleFormChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dată Sfârșit</Form.Label>
                  <Form.Control type="date" name="end_date" value={formState.end_date} onChange={handleFormChange} required />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-4">Itemuri Factură</h5>
            {formState.invoice_items.map((item, index) => (
              <Row key={index} className="g-2 mb-2">
                <Col md={5}>
                  <Form.Control
                    type="text"
                    name="description"
                    placeholder="Descriere"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, e)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <Form.Control
                    type="number"
                    name="quantity"
                    placeholder="Bucăți"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, e)}
                    min="1"
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    type="number"
                    name="price_per_item"
                    placeholder="Preț/buc. (opțional)"
                    value={item.price_per_item}
                    onChange={(e) => handleItemChange(index, e)}
                    min="0"
                  />
                </Col>
                <Col md={1} className="d-flex align-items-center">
                  <Button variant="outline-danger" onClick={() => handleRemoveItem(index)}>
                    &times;
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant="secondary" onClick={handleAddItem} className="mt-3">
              + Adaugă Item Nou
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Anulează
          </Button>
          <Button variant="primary" type="submit" onClick={handleSubmit}>
            {editingInvoice ? "Salvează Modificările" : "Creează Factură"}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default InvoicesPage;
